import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Icon from './Icon';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, listFrom, pick, useDrillAuth } from './drillHelpers';
import { exportPeriodFor } from '../utils/reportPeriod';
import { VoidsShimmer, LogCardShimmer, Bone } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import { getPaidVoidedOrders, getVoidedOrderSummary, getVoidAndCompSummary, todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import { useStoreTimeZone } from '../utils/dateTimeUtils';
import { getLiveVoidDetailsApi } from '../redux/reports/clickHouse/clickHouseApi';
import SectionEmpty from './reports/SectionEmpty';
import TruncatedCell from './reports/TruncatedCell';
import { getCurrencySymbol, moneyHeader } from '../utils/currency';
import { formatMonetaryExport } from '../utils/reportExport';
import { ReportSectionExportButton } from './reports/components/ReportExportButton';

type VoidPagination = {
    page: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    loading: boolean;
    onPageChange: (next: number) => void;
};

type StaffVoidAlert = { name: string; count: number; amount: number };
// Mirrors LiveVoidByStaffItem (see clickHouseInterface.ts) — kept loosely typed here like
// the rest of this file's props.
type StaffVoidRow = { staffName: string; removedQty: number; orderCount: number; cancelledAmt: number };
// Mirrors LiveVoidKpisResponse (see clickHouseInterface.ts) — kept loosely typed here like
// the rest of this file's props.
type VoidKpis = { voidedItems: number; voidValue: number; voidRate: number; avgVoidValue: number; itemsSold: number };

// Expand/collapse chevron for whole-check void groups in the Void Log — matches the
// desktop twin's Chev icon exactly.
const Chev = ({ s = 10, c = '#9CA3B8', r = 0 }: { s?: number; c?: string; r?: number }) => (
    <svg width={s} height={s} viewBox="0 0 10 10" style={{ transform: `rotate(${r}deg)`, transition: 'transform 0.2s', flexShrink: 0 }}>
        <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={c} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
);

const DrillVoids = ({ onBack, initialDateFilter, initialDate, prefetchedVoidLog, prefetchedVoidSummary, staffAlerts, pagination, voidReasonsLoading, voidReasonsFailure, onRetryVoidReasons, voidLogLoading, voidLogFailure, onRetryVoidLog, staffVoids, staffVoidsLoading, staffVoidsFailure, onRetryStaffVoids, voidKpis, voidKpisLoading, voidKpisFailure, snapshotValues: snapshotValuesProp }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; prefetchedVoidLog?: any[]; prefetchedVoidSummary?: any[]; staffAlerts?: StaffVoidAlert[]; pagination?: VoidPagination; voidReasonsLoading?: boolean; voidReasonsFailure?: boolean; onRetryVoidReasons?: () => void; voidLogLoading?: boolean; voidLogFailure?: boolean; onRetryVoidLog?: () => void; staffVoids?: StaffVoidRow[]; staffVoidsLoading?: boolean; staffVoidsFailure?: boolean; onRetryStaffVoids?: () => void; voidKpis?: VoidKpis | null; voidKpisLoading?: boolean; voidKpisFailure?: boolean; snapshotValues?: any } = {} as any) => {
    const isEmbedded = !!onBack;
    const hasPrefetched = prefetchedVoidLog !== undefined;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot?drill=voids', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const { locationId, token, selectedDate: drillDate, dateFilter: drillDateFilter, snapshotValues: snapshotValuesFromRoute } = useDrillAuth();
    // When embedded in the mobile snapshot drill the component is rendered inline (not navigated
    // to), so router location.state has no snapshotValues — fall back to the prop the parent passes.
    const snapshotValues = snapshotValuesProp ?? snapshotValuesFromRoute;
    const storeTz = useStoreTimeZone();
    const today = useMemo(() => todayStr(storeTz), [storeTz]);
    const yesterday = useMemo(() => yesterdayStr(storeTz), [storeTz]);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? drillDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? drillDateFilter ?? 'Today');
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    const exportDateRangeLabel = exportPeriodFor(activeDateFilter, dateRange)?.label ?? activeDateFilter;

    const [loading, setLoading] = useState(!hasPrefetched);
    const [voidSummaryData, setVoidSummaryData] = useState<any>(null);
    const [voidLog, setVoidLog] = useState<any[]>([]);
    const [historicalVoidLog, setHistoricalVoidLog] = useState<any[]>([]);
    const [reasonsPage, setReasonsPage] = useState(0);
    const REASONS_PAGE_SIZE = 5;
    // Whole-check void groups (same order number) expanded in the Void Log cards.
    const [expandedVoidGroups, setExpandedVoidGroups] = useState<Set<string>>(new Set());
    const toggleVoidGroup = (key: string) => setExpandedVoidGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    useEffect(() => { setExpandedVoidGroups(new Set()); }, [dateRange.startDate, dateRange.endDate]);

    const isToday = activeDateFilter === 'Today';

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        setReasonsPage(0);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Voids',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    useEffect(() => {
        // Skip own fetch when SnapshotScreen already passed pre-fetched data
        if (hasPrefetched) return;
        if (!locationId) {
            setLoading(false);
            return;
        }
        const opts = { locationId, startDate: dateRange.startDate, endDate: dateRange.endDate, token };
        setLoading(true);

        if (isToday) {
            // Today: use live paid-void-orders
            Promise.allSettled([
                getPaidVoidedOrders({ locationId, token, page: 0, size: 100, search: '' }),
            ]).then(([r0]) => {
                const raw = r0.status === 'fulfilled' ? r0.value : null;
                const rows = listFrom(raw);
                setVoidLog(rows);
                setVoidSummaryData(null);
                setHistoricalVoidLog([]);
                setLoading(false);
            });
        } else {
            // Historical: voidedSummary (reasons) + void-and-comp-summary (void log details)
            Promise.allSettled([
                getVoidedOrderSummary({ ...opts }),
                getVoidAndCompSummary({ ...opts, type: 'voided', reason: 'all', page: 0, size: 100 }),
            ]).then(([r0, r1]) => {
                const raw1 = r1.status === 'fulfilled' ? r1.value : null;
                const rows1 = listFrom(raw1);
                setVoidSummaryData(r0.status === 'fulfilled' ? r0.value : null);
                setHistoricalVoidLog(rows1);
                setVoidLog([]);
                setLoading(false);
            });
        }
    }, [hasPrefetched, locationId, token, dateRange.startDate, dateRange.endDate, isToday]);

    // Void summary: use pre-fetched data if available, otherwise own fetched data
    const voidSummaryList = hasPrefetched ? (prefetchedVoidSummary ?? []) : listFrom(voidSummaryData);
    const hasPrefetchedTodaySummary = isToday && hasPrefetched && voidSummaryList.length > 0;
    // Distinct orders: the backend's period-level totalOrders when present; summing per-reason
    // orderCount would count an order once per reason it was voided under.
    const summaryOrderCount = voidSummaryList.length > 0 && voidSummaryList[0].totalOrders != null
        ? Number(voidSummaryList[0].totalOrders)
        : voidSummaryList.reduce((s, v) => s + (Number(v.orderCount ?? 0) || 0), 0);
    const totalVoidCount = hasPrefetchedTodaySummary
        ? summaryOrderCount
        : isToday
        ? voidLog.length
        : (voidSummaryList.length > 0
            ? summaryOrderCount
            : (snapshotValues?.voidCount != null ? Number(snapshotValues.voidCount) : 0));
    const totalVoidAmount = hasPrefetchedTodaySummary
        ? voidSummaryList.reduce((s, v) => s + (Number(v.voidedAmount ?? v.cancelledAmt ?? 0) || 0), 0)
        : isToday
        ? voidLog.reduce((s, v) => s + (Number(v.voidAmount ?? v.amount ?? v.totalAmount ?? 0) || 0), 0)
        : (voidSummaryList.length > 0
            ? voidSummaryList.reduce((s, v) => s + (Number(v.voidedAmount ?? 0) || 0), 0)
            : (snapshotValues?.voidAmount != null ? Number(snapshotValues.voidAmount) : 0));
    // Use the parent-supplied voidCount (same key the desktop subtext uses: live order count
    // for Today, void-log totalElements for Historical) so the header count matches desktop.
    // The void-reasons summary's orderCount sums to a larger number because one order can
    // carry multiple void reasons, so it is NOT used for the header count.
    const voidCount = snapshotValues?.voidCount != null ? Number(snapshotValues.voidCount) : totalVoidCount;
    const totalAmount = totalVoidAmount > 0 ? totalVoidAmount : (snapshotValues?.voidAmount != null ? Number(snapshotValues.voidAmount) : 0);
    // Voided items + value come from /void-kpis; orders count stays the one derived above
    // (same source the desktop header uses) so the two surfaces can't disagree on order count.
    const subTitle = voidKpisFailure || !voidKpis
        ? (voidCount > 0 || totalAmount > 0 ? `${voidCount} voids · ${fmtCurrency(totalAmount)} total` : 'No voids')
        : `${voidKpis.voidedItems ?? 0} voided items · ${fmtCurrency(voidKpis.voidValue ?? 0)} total voided value · ${voidCount} orders`;

    // Void reasons: summary rows (prefetched or historical own-fetch) sorted desc by count;
    // Today own-fetch → aggregate from voidLog. All rows kept (no cap) — FE-paginated below
    // so mobile shows the same full set of reasons as desktop.
    const reasonRows = (hasPrefetchedTodaySummary || !isToday)
        ? (() => {
            // Void Reasons count = voided item quantity (removedQty), not distinct orders.
            const qtyOf = (v: any) => Number(v.removedQty ?? v.orderCount ?? 0) || 0;
            const sorted = [...voidSummaryList].sort((a, b) => qtyOf(b) - qtyOf(a));
            const maxCount = sorted.reduce((m, v) => Math.max(m, qtyOf(v)), 1);
            return sorted.map((v) => {
                const count = qtyOf(v);
                return {
                    r: v.voidedReasons ?? v.cancelReason ?? v.reason ?? 'Unknown',
                    c: count,
                    p: Math.round((count / maxCount) * 100),
                };
            });
        })()
        : (() => {
            const reasonMap: Record<string, any> = voidLog.reduce((acc: any, v: any) => {
                const r = v.reason ?? v.voidReason ?? 'Unknown';
                if (!acc[r]) acc[r] = { r, c: 0 };
                acc[r].c++;
                return acc;
            }, {});
            const rows: any[] = Object.values(reasonMap).sort((a: any, b: any) => b.c - a.c);
            const total = rows.reduce((s: number, x: any) => s + x.c, 0) || 1;
            return rows.map((x: any) => ({ ...x, p: Math.round((x.c / total) * 100) }));
        })();
    const reasonsTotalPages = Math.max(1, Math.ceil(reasonRows.length / REASONS_PAGE_SIZE));
    // Clamp instead of resetting via effect so a shrinking list can't strand the page out of range.
    const reasonsSafePage = Math.min(reasonsPage, reasonsTotalPages - 1);
    const pagedReasonRows = reasonRows.slice(reasonsSafePage * REASONS_PAGE_SIZE, (reasonsSafePage + 1) * REASONS_PAGE_SIZE);

    // Clean item name: "Chicken lasagna-1.0000" → "Chicken lasagna", "Small Onion Uthappam-2.0000" → "Small Onion Uthappam (x2)"
    const cleanItem = (raw: any) => {
        if (!raw || raw === '—') return '—';
        const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
        const cleaned = parts.map((part) => {
            const match = part.match(/^(.+?)-(\d+(?:\.\d+)?)$/);
            if (match) {
                const name = match[1].trim();
                const qty = Math.round(Number(match[2]));
                return qty > 1 ? `${name} (x${qty})` : name;
            }
            return part;
        });
        return cleaned.length > 2 ? `${cleaned[0]}, ${cleaned[1]} +${cleaned.length - 2} more` : cleaned.join(', ');
    };

    // Void log rows: use pre-fetched if available, otherwise own fetched data.
    // Pre-fetched already has the same logic as desktop (voidDetail → cancelDetail fallback → summaryExpansion).
    const effectiveVoidLog = hasPrefetched ? (prefetchedVoidLog ?? []) : (() => {
        if (isToday) return voidLog;
        if (historicalVoidLog.length > 0) return historicalVoidLog;
        if (voidSummaryList.length === 0) return [];
        return voidSummaryList.flatMap((v: any) => {
            const count = Math.max(1, Math.round(Number(v.orderCount ?? 1)));
            const amtEach = Number(v.voidedAmount ?? 0) / count;
            return Array.from({ length: count }, () => ({
                ...v,
                voidedAmount: amtEach,
                reason: v.voidedReasons ?? v.reason,
            }));
        });
    })();

    // Voids by Staff alert — when the parent (SnapshotScreen) already aggregated the full,
    // unpaginated void log it passes `staffAlerts` directly. Otherwise (standalone fetch path)
    // derive it here from `effectiveVoidLog`, which in that path IS the full period log.
    const computedStaffAlerts = useMemo<StaffVoidAlert[]>(() => {
        if (staffAlerts) return staffAlerts;
        const map: Record<string, StaffVoidAlert> = {};
        effectiveVoidLog.forEach((v: any) => {
            const name = String(v.steward ?? v.staffName ?? v.staff ?? v.employeeName ?? v.employee ?? '').trim();
            if (!name) return;
            if (!map[name]) map[name] = { name, count: 0, amount: 0 };
            map[name].count += Number(v.voidedQty) || 1; // voided item quantity, not log rows
            map[name].amount += Number(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.cancelledAmt ?? 0) || 0;
        });
        return Object.values(map).filter((s) => s.count >= 2).sort((a, b) => b.count - a.count);
    }, [staffAlerts, effectiveVoidLog]);

    // When the parent provides backend pagination, `effectiveVoidLog` is already
    // the current page — don't slice. Otherwise cap at 10 (legacy un-paginated flow).
    const hasPagination = !!pagination;
    const todaySource = hasPrefetched ? effectiveVoidLog : voidLog;
    const logRows = isToday
        ? (hasPagination ? todaySource : todaySource.slice(0, 10)).map((v) => ({
            item: cleanItem(v.voidedItems ?? v.itemName ?? v.item ?? v.menuItemName ?? v.name ?? v.productName ?? '—'),
            rawAmount: Number(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.cancelledAmt ?? 0) || 0,
            amount: fmtCurrency(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.cancelledAmt ?? 0),
            time: v.ordertime ?? v.voidTime ?? v.time ?? v.voidedTime ?? v.date ?? v.voidDate ?? v.createdAt ?? '—',
            reason: v.reason ?? v.voidReason ?? v.voidedReasons ?? v.reasonDescription ?? v.cancelReason ?? '—',
            staff: v.steward ?? v.staffName ?? v.staff ?? v.employeeName ?? v.employee ?? '—',
            orderNo: v.orderNumber ?? v.orderNo ?? v.order_no ?? v.orderId ?? v.order_id ?? '',
        }))
        : (hasPagination ? effectiveVoidLog : effectiveVoidLog.slice(0, 10)).map((v: any) => ({
            item: cleanItem(v.voidedItems ?? v.itemName ?? v.item ?? v.menuItemName ?? '—'),
            rawAmount: Number(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.cancelledAmt ?? 0) || 0,
            amount: fmtCurrency(v.voidedAmount ?? v.voidAmount ?? v.amount ?? v.totalAmount ?? v.cancelledAmt ?? 0),
            time: v.voidTime ?? v.ordertime ?? v.time ?? v.voidedTime ?? v.date ?? '—',
            reason: v.reason ?? v.voidReason ?? v.voidedReasons ?? v.cancelReason ?? '—',
            staff: v.steward ?? v.staffName ?? v.staff ?? v.employeeName ?? '—',
            orderNo: v.orderNumber ?? v.orderNo ?? v.order_no ?? v.orderId ?? v.order_id ?? '',
            qty: Number(v.voidedQty ?? v.refundedQuantity ?? v.refundQuantity ?? v.voidedQuantity ?? v.quantity ?? v.qty ?? 0) || 0,
            date: v.orderDate ?? '—',
        }));

    // Whole-check voids: every voided item sharing an order number collapses into one card
    // (order #, staff, reason, item count, combined amount), expandable to each item's own
    // price. Single-item orders render exactly as before. Each item still counts individually
    // toward Voided Items and the Voids by Staff ranking — this only groups the log's own display.
    type VoidLogGroup = { key: string; orderNo: string; staff: string; amount: number; time: string; date?: string; items: any[] };
    const logGroups: VoidLogGroup[] = (() => {
        const map = new Map<string, VoidLogGroup>();
        const order: string[] = [];
        logRows.forEach((v: any, i: number) => {
            const key = v.orderNo || `_${i}`;
            if (!map.has(key)) {
                map.set(key, { key, orderNo: v.orderNo, staff: v.staff, amount: 0, time: v.time, date: v.date, items: [] });
                order.push(key);
            }
            const g = map.get(key)!;
            g.amount += v.rawAmount;
            g.items.push(v);
        });
        return order.map((k) => map.get(k)!);
    })();

    // `voidedTime` already arrives formatted in the store's own 12-hour clock
    // ("02:39 PM") — show it exactly as the API sends it. It must NOT be re-derived:
    // `new Date("02:39 PM")` is Invalid Date, and the old regex fallback captured only
    // HH:MM and recomputed the meridiem from the hour digits, so every PM void rendered
    // as AM (and 12:xx AM as PM). A full timestamp would be just as wrong the other way —
    // `toLocaleTimeString()` renders it in the *device's* timezone, not the store's.
    const fmtVoidTime = (raw: any): string => {
        if (raw == null) return '';
        const s = String(raw).trim();
        return s === '—' ? '' : s;
    };

    // While the parent refetches the void summary/log (date/channel change) the cleared redux
    // arrays would make the header read "No voids" (or show the previous range's snapshotValues)
    // — shimmer it until the new data lands (SSE-18).
    const headerLoading = !!(voidReasonsLoading || voidLogLoading || voidKpisLoading || (pagination && pagination.loading));
    // Unified empty state — gated on /void-kpis having actually resolved to zero (not merely
    // loading or failed, where the section-level shimmers/errors below still apply).
    const voidsEmpty = !voidKpisLoading && !voidKpisFailure && Number(voidKpis?.voidedItems ?? 0) === 0;
    const inner = loading ? <VoidsShimmer /> : (<>
        <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{headerLoading ? <Bone w={140} h={12} r={4} /> : subTitle}</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
                {/* Mini-KPIs — /void-kpis */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                        { l: 'Voided Items', v: voidKpisFailure ? '--' : String(voidKpis?.voidedItems ?? 0) },
                        { l: 'Void Value', v: voidKpisFailure ? '--' : fmtCurrency(voidKpis?.voidValue ?? 0) },
                        { l: 'Void Rate', v: voidKpisFailure ? '--' : `${Number(voidKpis?.voidRate ?? 0).toFixed(1)}%` },
                        { l: 'Avg Void Value', v: voidKpisFailure ? '--' : fmtCurrency(voidKpis?.avgVoidValue ?? 0) },
                    ].map((k, i) => (
                        <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '8px 6px', textAlign: 'center', boxShadow: C.shadow }}>
                            <div style={{ fontSize: 7, color: C.t3, textTransform: 'uppercase' }}>{k.l}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: C.t1, marginTop: 2 }}>
                                {voidKpisLoading ? <Bone w={40} h={16} r={4} style={{ margin: '0 auto' }} /> : k.v}
                            </div>
                        </div>
                    ))}
                </div>
                {voidsEmpty ? (
                    <SectionEmpty kpiName="voids" size="medium" message="No voids — nothing was removed from a check after firing." />
                ) : (
                <>
                {!headerLoading && computedStaffAlerts.map((s) => (
                    <div key={s.name} style={{ background: `${C.orange ?? '#f97316'}18`, border: `1px solid ${(C.orange ?? '#f97316')}44`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 11, color: C.t1 }}>
                        <span style={{ fontWeight: 600, color: C.orange ?? '#f97316' }}>⚠ {s.name}</span> voided {s.count} items ({fmtCurrency(s.amount)} total). Consider reviewing with manager.
                    </div>
                ))}
                {/* Void Reasons */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Void Reasons</div>
                    {voidReasonsLoading ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ marginBottom: 8 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                        <Bone w={90} h={11} r={4} />
                                        <Bone w={24} h={11} r={4} />
                                    </div>
                                    <Bone w={null} h={5} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : voidReasonsFailure ? (
                        <div style={{ padding: '12px 0', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: C.t2, marginBottom: 8 }}>Unable to load void reasons. Please try again.</div>
                            {onRetryVoidReasons && (
                                <button type="button" onClick={onRetryVoidReasons} style={{ fontSize: 11, fontWeight: 600, color: C.t1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 6, padding: '4px 14px', cursor: 'pointer' }}>Retry</button>
                            )}
                        </div>
                    ) : reasonRows.length ? (<>
                        {pagedReasonRows.map((r, i) => (
                        <div key={i} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 11, color: C.t2 }}>{r.r}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{r.c}x</span>
                            </div>
                            <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${r.p}%`, background: C.orange, borderRadius: 3, opacity: 0.6 }} />
                            </div>
                        </div>
                        ))}
                        {reasonsTotalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                <span style={{ fontSize: 10, color: C.t3 }}>
                                    {reasonsSafePage * REASONS_PAGE_SIZE + 1}–{Math.min((reasonsSafePage + 1) * REASONS_PAGE_SIZE, reasonRows.length)} of {reasonRows.length}
                                </span>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <button onClick={() => setReasonsPage(Math.max(0, reasonsSafePage - 1))} disabled={reasonsSafePage === 0} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: reasonsSafePage === 0 ? C.hover : C.card, color: reasonsSafePage === 0 ? C.t3 : C.t1, fontSize: 10, fontWeight: 600, cursor: reasonsSafePage === 0 ? 'default' : 'pointer' }}>← Prev</button>
                                    <span style={{ fontSize: 10, color: C.t2, padding: '4px 4px' }}>{reasonsSafePage + 1} / {reasonsTotalPages}</span>
                                    <button onClick={() => setReasonsPage(Math.min(reasonsTotalPages - 1, reasonsSafePage + 1))} disabled={reasonsSafePage >= reasonsTotalPages - 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: reasonsSafePage >= reasonsTotalPages - 1 ? C.hover : C.card, color: reasonsSafePage >= reasonsTotalPages - 1 ? C.t3 : C.t1, fontSize: 10, fontWeight: 600, cursor: reasonsSafePage >= reasonsTotalPages - 1 ? 'default' : 'pointer' }}>Next →</button>
                                </div>
                            </div>
                        )}
                    </>) : (
                        <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 11, color: C.t3 }}>{loading ? <Bone w={80} h={11} r={4} style={{ margin: '0 auto' }} /> : 'No void reasons'}</div>
                    )}
                </div>

                {/* Void Log — shown for both Today and Historical */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Void Log</div>
                    {logRows.length > 0 && (
                        <ReportSectionExportButton
                            dateRangeLabel={exportDateRangeLabel}
                            title="Void Log"
                            filename="snapshot-void-log"
                            buildExport={async (_format, opts) => {
                                const targetLocationId = opts?.locationId ?? locationId;
                                const dateParams = isToday ? {} : { startDate: dateRange.startDate, endDate: dateRange.endDate };
                                let total = pagination?.totalElements || voidCount || 1;
                                if (opts?.locationId) {
                                    const probe: any = await getLiveVoidDetailsApi({ locationId: targetLocationId, page: 1, size: 1, ...dateParams, token });
                                    total = probe?.totalElements || 1;
                                }
                                const res: any = await getLiveVoidDetailsApi({ locationId: targetLocationId, page: 1, size: total, ...dateParams, token });
                                const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []);
                                if (isToday) {
                                    return {
                                        headers: ['Order #', 'Time', 'Item', 'Staff', 'Reason', moneyHeader('Amount')],
                                        rows: list.map((v: any) => [
                                            `#${v.orderNo ?? '—'}`,
                                            fmtVoidTime(v.voidedTime ?? '—') || '—',
                                            v.itemName || '—',
                                            v.staffName || '—',
                                            v.cancelReason || '—',
                                            formatMonetaryExport(v.cancelledAmt ?? 0),
                                        ]),
                                    };
                                }
                                return {
                                    headers: ['Order #', 'Date', 'Item', 'Staff', 'Reason', moneyHeader('Amount')],
                                    rows: list.map((v: any) => [
                                        `#${v.orderNo ?? '—'}`,
                                        v.orderDate ?? '—',
                                        v.itemName || '—',
                                        v.staffName || '—',
                                        v.cancelReason || '—',
                                        formatMonetaryExport(v.cancelledAmt ?? 0),
                                    ]),
                                };
                            }}
                        />
                    )}
                </div>
                {(voidLogLoading || (hasPagination && pagination?.loading)) ? (
                    <LogCardShimmer count={3} />
                ) : voidLogFailure ? (
                    <div style={{ padding: '16px 0', textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: C.t2, marginBottom: 8 }}>Unable to load void log. Please try again.</div>
                        {onRetryVoidLog && (
                            <button type="button" onClick={onRetryVoidLog} style={{ fontSize: 11, fontWeight: 600, color: C.t1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 6, padding: '4px 14px', cursor: 'pointer' }}>Retry</button>
                        )}
                    </div>
                ) : logGroups.length ? logGroups.map((g) => {
                    const isGroup = g.items.length > 1;
                    const isExpanded = expandedVoidGroups.has(g.key);
                    const timeDisp = fmtVoidTime(g.time);
                    const amountStr = fmtCurrency(g.amount);
                    const groupReasons = Array.from(new Set(g.items.map((it: any) => it.reason || '—')));
                    const reasonLabel = groupReasons.length > 1 ? `${groupReasons.length} reasons` : groupReasons[0];
                    if (isToday) {
                        return (
                            <div key={g.key} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px 12px', marginBottom: 5, boxShadow: C.shadow, overflow: 'hidden' }}>
                                <div onClick={isGroup ? () => toggleVoidGroup(g.key) : undefined} style={{ cursor: isGroup ? 'pointer' : 'default' }}>
                                    {g.orderNo && (
                                        <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {isGroup && <Chev s={9} c={C.t3} r={isExpanded ? 180 : 0} />}
                                            #{g.orderNo}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.accent, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.staff}</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.orange, flexShrink: 0 }}>{amountStr}</span>
                                    </div>
                                    <div title={groupReasons.join(', ')} style={{ fontSize: 10, color: C.t2, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reasonLabel}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                        <TruncatedCell isMobile text={isGroup ? `${g.items.length} items` : g.items[0].item} style={{ fontSize: 11, fontWeight: 600, color: C.t1, maxWidth: '60%' }} />
                                        {timeDisp && <span style={{ fontSize: 9, color: C.t3, padding: '2px 6px', borderRadius: 4, background: C.hover, flexShrink: 0 }}>{timeDisp}</span>}
                                    </div>
                                </div>
                                {isGroup && isExpanded && (
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                        {g.items.map((item, ii) => (
                                            <div key={ii} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: ii < g.items.length - 1 ? '0 0 6px' : 0 }}>
                                                <TruncatedCell isMobile text={item.item} style={{ fontSize: 10, color: C.t2, maxWidth: '60%' }} />
                                                <span style={{ fontSize: 10, fontWeight: 500, color: C.t2, flexShrink: 0 }}>{item.amount}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    const first = g.items[0];
                    return (
                        <div key={g.key} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '12px 12px', marginBottom: 5, boxShadow: C.shadow, overflow: 'hidden' }}>
                            <div onClick={isGroup ? () => toggleVoidGroup(g.key) : undefined} style={{ cursor: isGroup ? 'pointer' : 'default' }}>
                                {g.orderNo ? (
                                    <div style={{ fontSize: 10, fontWeight: 600, color: C.t2, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {isGroup && <Chev s={9} c={C.t3} r={isExpanded ? 180 : 0} />}
                                        #{g.orderNo}
                                    </div>
                                ) : null}
                                {g.date && g.date !== '—' && <div style={{ fontSize: 10, color: C.t3, marginBottom: 4 }}>{g.date}</div>}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 3 }}>
                                    <TruncatedCell isMobile text={isGroup ? `${g.items.length} items` : `${first.item}${first.qty > 1 ? ` (x${first.qty})` : ''}`} style={{ fontSize: 13, fontWeight: 600, color: C.t1, maxWidth: '60%' }} />
                                    <span style={{ fontSize: 13, fontWeight: 600, color: C.orange, flexShrink: 0 }}>{amountStr}</span>
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.staff}</div>
                                <div title={groupReasons.join(', ')} style={{ fontSize: 10, color: C.t2, marginBottom: timeDisp ? 6 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reasonLabel}</div>
                                {timeDisp && (
                                    <span style={{ display: 'inline-block', fontSize: 9, color: C.t3, padding: '2px 6px', borderRadius: 4, background: C.hover }}>{timeDisp}</span>
                                )}
                            </div>
                            {isGroup && isExpanded && (
                                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                                    {g.items.map((item, ii) => (
                                        <div key={ii} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: ii < g.items.length - 1 ? '0 0 6px' : 0 }}>
                                            <TruncatedCell isMobile text={`${item.item}${item.qty > 1 ? ` (x${item.qty})` : ''}`} style={{ fontSize: 11, color: C.t2, maxWidth: '60%' }} />
                                            <span style={{ fontSize: 11, fontWeight: 500, color: C.t2, flexShrink: 0 }}>{item.amount}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }) : (
                    <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 11, color: C.t3 }}>{loading ? <Bone w={80} h={11} r={4} style={{ margin: '0 auto' }} /> : 'No void log'}</div>
                )}

                {/* Pagination footer */}
                {hasPagination && pagination && pagination.totalPages > 1 && (
                    pagination.loading ? (
                        // While a page is loading, swap the controls for a shimmer so the
                        // user can't change pages mid-fetch.
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                            <Bone w={90} h={12} />
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <Bone w={56} h={24} r={6} />
                                <Bone w={36} h={12} />
                                <Bone w={56} h={24} r={6} />
                            </div>
                        </div>
                    ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.divider}` }}>
                        <span style={{ fontSize: 10, color: C.t3 }}>
                            {pagination.page * pagination.pageSize + 1}–{Math.min((pagination.page + 1) * pagination.pageSize, pagination.totalElements)} of {pagination.totalElements}
                        </span>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <button onClick={() => pagination.onPageChange(Math.max(0, pagination.page - 1))} disabled={pagination.page === 0} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: pagination.page === 0 ? C.hover : C.card, color: pagination.page === 0 ? C.t3 : C.t1, fontSize: 10, fontWeight: 600, cursor: pagination.page === 0 ? 'default' : 'pointer' }}>← Prev</button>
                            <span style={{ fontSize: 10, color: C.t2, padding: '4px 4px' }}>{pagination.page + 1} / {pagination.totalPages}</span>
                            <button onClick={() => pagination.onPageChange(Math.min(pagination.totalPages - 1, pagination.page + 1))} disabled={pagination.page >= pagination.totalPages - 1} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: pagination.page >= pagination.totalPages - 1 ? C.hover : C.card, color: pagination.page >= pagination.totalPages - 1 ? C.t3 : C.t1, fontSize: 10, fontWeight: 600, cursor: pagination.page >= pagination.totalPages - 1 ? 'default' : 'pointer' }}>Next →</button>
                        </div>
                    </div>
                    )
                )}

                {/* Voids by Staff — /void-staff (NOT YET DEPLOYED, see getVoidByStaffApi) */}
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginTop: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 2 }}>Voids by Staff</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Staff members with the most voided items</div>
                    {staffVoidsLoading ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ marginBottom: 8 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                        <Bone w={90} h={11} r={4} />
                                        <Bone w={50} h={11} r={4} />
                                    </div>
                                    <Bone w={null} h={5} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : staffVoidsFailure ? (
                        <div style={{ padding: '12px 0', textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: C.t2, marginBottom: 8 }}>Unable to load voids by staff. Please try again.</div>
                            {onRetryStaffVoids && (
                                <button type="button" onClick={onRetryStaffVoids} style={{ fontSize: 11, fontWeight: 600, color: C.t1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 6, padding: '4px 14px', cursor: 'pointer' }}>Retry</button>
                            )}
                        </div>
                    ) : staffVoids && staffVoids.length ? (() => {
                        const sorted = [...staffVoids].sort((a, b) => b.cancelledAmt - a.cancelledAmt);
                        const maxAmt = sorted.reduce((m, s) => Math.max(m, s.cancelledAmt), 1);
                        return sorted.map((s, i) => (
                            <div key={i} style={{ marginBottom: i < sorted.length - 1 ? 10 : 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.accent }}>{s.staffName}</span>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ fontSize: 10, color: C.t3 }}>{s.removedQty} items</span>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{fmtCurrency(s.cancelledAmt)}</span>
                                    </div>
                                </div>
                                <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.min((s.cancelledAmt / maxAmt) * 100, 100)}%`, background: C.orange, borderRadius: 3, opacity: 0.7 }} />
                                </div>
                            </div>
                        ));
                    })() : (
                        <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 11, color: C.t3 }}>No voids by staff</div>
                    )}
                </div>
                </>
                )}

        </div>
    </>);

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillVoids;
